import React from 'react';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import { useTheme } from '@mui/material/styles';
import { Badge, Typography } from '@mui/material';
import { CompletedIcon, ContinueIcon, PlayCircleIcon } from './CustomIcons';
import {
  KitCard,
  KitCardBody,
  KitButton,
} from '@boystownorg/bi-cms-component-lib';
import { hasNewModuleNotStarted } from '../../services/gatedResourceUtils';

const GatedModuleCard = (props) => {
  const { module, hasAccess } = props;

  const router = useRouter();
  const theme = useTheme();

  const StateContainer = styled.div`
    width: 75%;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-content: center;
    margin-bottom: 15px;
    cursor: pointer;
  `;

  const StateIcon = styled.div`
    display: inline-block;
    margin: 0 5px 0 0;
  `;

  const StateText = styled.div`
    display: inline-block;
    font-size: 0.813rem;
  `;

  return (
    <KitCard style={{ width: '23rem' }}>
      <React.Fragment>
        <KitCardBody style={{ padding: '1.00rem' }}>
          {module.fields.moduleImage && (
            <ImageContainer
              style={{
                backgroundImage: `url(${module?.fields?.moduleImage?.url})`,
              }}
            ></ImageContainer>
          )}
          <CardBodyContainer>
            <ModuleName>{module.fields?.moduleName}</ModuleName>
          </CardBodyContainer>
          {module.fields.comingSoon !== 'true' && (
            <StateContainer onClick={() => router.push(module.url)}>
              <StateIcon>
                {module.state === 'Start' ? (
                  <PlayCircleIcon
                    onClick={() => router.push(module.url)}
                    sx={{
                      color: theme.palette.tertiary.main,
                      height: '1.15em',
                      width: '1.15em',
                    }}
                  />
                ) : module.state === 'Continue' ? (
                  <ContinueIcon
                    onClick={() => router.push(module.url)}
                    sx={{ color: '#F2A900', height: '1.25em', width: '1.25em' }}
                  />
                ) : (
                  <CompletedIcon
                    onClick={() => router.push(module.url)}
                    sx={{ color: '#B7BF10', height: '1.25em', width: '1.25em' }}
                  />
                )}
              </StateIcon>
              <StateText>{module.state}</StateText>
            </StateContainer>
          )}
          {module.fields.comingSoon === 'true' && (
            <div style={{ marginTop: 12 }}></div>
          )}
          <LaunchButtonContainer>
            {/* View Modules Button Only; If no module is updated or none are new or all have been started. */}
            {module.fields.comingSoon !== 'true' &&
              module?.subMods?.length > 0 &&
              module?.url &&
              !module.hasNewVersion &&
              (!module.isNew || hasNewModuleNotStarted(module) === 0) && (
                <KitButton
                  size="sm"
                  round
                  color="success"
                  onClick={() => router.push(module.url)}
                >
                  {hasAccess ? 'View Modules' : 'See More'}
                </KitButton>
              )}

            {/* Updated: Show if any module is updated, and none are new, or one is new, but all have been started. */}
            {module.fields.comingSoon !== 'true' &&
              module?.subMods?.length > 0 &&
              module?.url &&
              module.hasNewVersion &&
              (!module.isNew ||
                (module.isNew && hasNewModuleNotStarted(module) === 0)) && (
                <Badge
                  badgeContent={'UPDATED'}
                  color="info"
                  sx={{
                    '& span.MuiBadge-colorInfo': {
                      backgroundColor: theme.palette.info.light,
                    },
                    '& span.MuiBadge-standard': {
                      borderRadius: '5px',
                      width: '80px',
                      borderLeft: '2px solid #fff',
                      borderTop: '2px solid #fff',
                      borderBottom: '2px solid #fff',
                      paddingRight: '10px',
                    },
                    '& span.MuiBadge-standard::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      widht: 0,
                      height: 0,
                      borderLeft: '16px solid transparent',
                      borderTop: '16px solid #fff',
                      clipPath: 'polygon(100% 0, 0 100%, 100% 100%)',
                      marginRight: '-1px',
                    },
                  }}
                >
                  <KitButton
                    size="sm"
                    round
                    color="success"
                    onClick={() => router.push(module.url)}
                  >
                    {hasAccess ? 'View Modules' : 'See More'}
                  </KitButton>
                </Badge>
              )}

            {/* New: If any module is new and at least one has not been started. */}
            {module.fields.comingSoon !== 'true' &&
              module?.subMods?.length > 0 &&
              module?.url &&
              module.isNew &&
              hasNewModuleNotStarted(module) > 0 && (
                <Badge
                  badgeContent={'NEW'}
                  color="info"
                  sx={{
                    '& span.MuiBadge-colorInfo': {
                      backgroundColor: theme.palette.info.light,
                    },
                    '& span.MuiBadge-standard': {
                      borderRadius: '5px',
                      width: '80px',
                      borderLeft: '2px solid #fff',
                      borderTop: '2px solid #fff',
                      borderBottom: '2px solid #fff',
                      paddingRight: '10px',
                    },
                    '& span.MuiBadge-standard::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      widht: 0,
                      height: 0,
                      borderLeft: '16px solid transparent',
                      borderTop: '16px solid #fff',
                      clipPath: 'polygon(100% 0, 0 100%, 100% 100%)',
                      marginRight: '-1px',
                    },
                  }}
                >
                  <KitButton
                    size="sm"
                    round
                    color="success"
                    onClick={() => router.push(module.url)}
                  >
                    {hasAccess ? 'View Modules' : 'See More'}
                  </KitButton>
                </Badge>
              )}
            {(module.fields.comingSoon === 'true' ||
              module.subMods.length === 0) && (
              <Typography
                style={{ marginTop: 9 }}
                sx={{ color: theme.palette.brandedAqua.main }}
              >
                Coming Soon
              </Typography>
            )}
          </LaunchButtonContainer>
        </KitCardBody>
      </React.Fragment>
    </KitCard>
  );
};

export default GatedModuleCard;

const ImageContainer = styled('div')(({ theme }) => ({
  width: '100%',
  height: '104px',
  backgroundSize: '100%',
  backgroundPosition: 'right 30% top 35%',
  backgroundRepeat: 'no-repeat',
}));

const CardBodyContainer = styled('div')(({ theme }) => ({
  padding: '10px 0 10px 0',
  minHeight: '50px',
}));

const ModuleName = styled('div')(({ theme }) => ({
  color: theme.palette.common.black,
  backgroundColor: 'transparent',
  margin: '5px 0 15px 0',
  fontSize: '1.063rem',
  lineHeight: '1.313rem',
  fontWeight: 600,
}));

const LaunchButtonContainer = styled('div')(() => {
  return {
    borderTop: '1px solid #ccc',
    display: 'flex',
    justifyContent: 'flex-start',
    paddingTop: '15px',
    '& button': {
      width: '150px',
    },
  };
});
